import React from 'react';

import NextImage, { ImageLoaderProps } from 'next/image';

import { BlogAuthorEntry } from '../../../api/contentful/blog/getBlogAuthor';
import {
  ContentfulResizeTypes,
  getContentfulLoader,
} from '../../../helpers/imageLoaders/contentful';
import { getPageUrl, getUserUrl } from '../../../helpers/urls';
import { InternalLink } from '../../Clickables/InternalLink/InternalLink';
import { Heading } from '../../Heading/Heading';
import { ArrowRightIcon } from '../../Icons/ArrowRightIcon';
import { Text } from '../../Text/Text';
import { BlogAuthorLink } from '../BlogAuthorButton/BlogAuthorLink';

interface IProps {
  author: BlogAuthorEntry;
}

/*
  Author can be fishbrain user, company, or neither. There need to be compresensive
  checks in place to ensure that we are not serving broken links
*/

export const BlogAuthor = ({ author }: IProps) => {
  const { name, authorImage, brandExternalId, brandName, fishbrainUsername, slug } = author.fields;
  const authorImageUrl = authorImage?.fields.file?.url;

  // Checking link text
  const userLinkText = fishbrainUsername ? `@${fishbrainUsername}` : undefined;
  const brandLinkText = brandName ? `@${name}` : undefined;
  const linkText = userLinkText || brandLinkText;

  // Checking page link
  const userLink = (fishbrainUsername && getUserUrl(fishbrainUsername)) || undefined;
  const brandLink =
    (brandExternalId && brandName && getPageUrl(brandExternalId, brandName)) || undefined;
  const pageLink = userLink || brandLink;

  const ASPECTWIDTH = 200;
  const ASPECTHEIGTH = 200;

  const contentfulLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getContentfulLoader(src, width, ASPECTWIDTH, ASPECTHEIGTH, {
      quality,
      resize: ContentfulResizeTypes.FILL,
    });

  return (
    <div>
      {slug && name && <BlogAuthorLink blogSlug={slug} authorNickname={name} />}

      {pageLink && (
        <InternalLink
          to={pageLink}
          data-testid="blog author page link"
          className="bg-sea-foam mt-4 flex rounded-lg p-4 shadow-[rgba(0,0,0.5,0.2)_2px_2px_5px_0px] sm:w-1/2 md:w-full md:items-center"
        >
          {authorImageUrl && (
            <div className="mr-2 w-14 sm:w-20 md:mr-4">
              <div className="relative h-14 w-full sm:h-20">
                <NextImage
                  alt={name}
                  src={authorImageUrl}
                  loader={contentfulLoader}
                  quality={75}
                  fill
                  className="size-full rounded-full object-cover"
                  sizes="20vw"
                />
              </div>
            </div>
          )}
          <div className="flex w-3/4 flex-col">
            <Heading as="h4" variant="sm" className="mb-1">
              {name}
            </Heading>
            <Text className="mb-0">{linkText}</Text>
          </div>

          <ArrowRightIcon size={20} className="my-auto" />
        </InternalLink>
      )}
    </div>
  );
};
